import {
  AfterViewInit, Component, ElementRef, EventEmitter, Input,
  NgZone, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild
} from '@angular/core';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

/** Posición de cámara y objetivo, para sincronizar dos visores. */
export interface StlCameraPose {
  px: number; py: number; pz: number;
  tx: number; ty: number; tz: number;
}

@Component({
  selector: 'app-stl-viewer',
  templateUrl: './stl-viewer.component.html',
  styleUrls: ['./stl-viewer.component.css']
})
export class StlViewerComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('canvas') private canvasRef: ElementRef<HTMLCanvasElement>;
  @Input() url: string;

  /** Emite el movimiento de cámara para que otro visor pueda replicarlo (sincronización). */
  @Output() cameraChange = new EventEmitter<StlCameraPose>();

  /** Identificador para agrupar visores que deben sincronizarse entre sí (p.ej. misma arcada). */
  @Input() syncKey: any = null;

  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private controls: OrbitControls;
  private animationId: number;
  private currentMesh: THREE.Mesh | null = null;
  private pendingUrl: string | null = null;
  private resizeObserver: ResizeObserver;

  // Sincronización de cámara entre visores
  private applyingExternal = false;
  private pendingPose: StlCameraPose | null = null;

  public loading = false;
  public errorMsg = '';

  constructor(private ngZone: NgZone) {}

  /**
   * Aplica una posición de cámara recibida desde otro visor.
   * No re-emite el cambio, para evitar bucles de sincronización.
   */
  public applyExternalPose(pose: StlCameraPose): void {
    if (!pose) { return; }
    if (!this.camera || !this.controls) { this.pendingPose = pose; return; }
    this.ngZone.runOutsideAngular(() => {
      this.applyingExternal = true;
      this.camera.position.set(pose.px, pose.py, pose.pz);
      this.controls.target.set(pose.tx, pose.ty, pose.tz);
      this.camera.updateProjectionMatrix();
      this.controls.update();
      this.applyingExternal = false;
    });
  }

  ngAfterViewInit(): void {
    const wrapper = this.canvasRef.nativeElement.parentElement!;

    this.resizeObserver = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      if (width === 0 || height === 0) return;

      if (!this.renderer) {
        this.ngZone.runOutsideAngular(() => {
          this.initThree(width, height);
          const url = this.pendingUrl ?? this.url;
          if (url) this.loadSTL(url);
          if (this.pendingPose) { this.applyExternalPose(this.pendingPose); this.pendingPose = null; }
        });
      } else {
        this.onResize(width, height);
      }
    });

    this.resizeObserver.observe(wrapper);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['url']) return;
    if (!this.renderer) {
      this.pendingUrl = this.url;
    } else {
      this.loadSTL(this.url);
    }
  }

  private initThree(w: number, h: number): void {
    const canvas = this.canvasRef.nativeElement;

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    this.renderer.setSize(w, h, false);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xF8F8F9);

    this.camera = new THREE.PerspectiveCamera(45, w / h, 0.01, 5000);
    this.camera.position.set(0, 0, 100);

    const ambient = new THREE.AmbientLight(0xffffff, 0.7);
    this.scene.add(ambient);
    const dir1 = new THREE.DirectionalLight(0xffffff, 0.8);
    dir1.position.set(1, 2, 3);
    this.scene.add(dir1);
    const dir2 = new THREE.DirectionalLight(0xffffff, 0.4);
    dir2.position.set(-1, -1, -2);
    this.scene.add(dir2);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.1;

    // Notifica el movimiento de cámara para sincronizar con otro visor.
    // Se omite cuando el cambio proviene de otro visor (evita bucles).
    this.controls.addEventListener('change', () => {
      if (this.applyingExternal) { return; }
      const p = this.camera.position;
      const t = this.controls.target;
      this.cameraChange.emit({ px: p.x, py: p.y, pz: p.z, tx: t.x, ty: t.y, tz: t.z });
    });

    this.animate();
  }

  private onResize(w: number, h: number): void {
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    if (this.currentMesh) this.fitCamera();
  }

  private loadSTL(url: string): void {
    if (!url) return;
    this.pendingUrl = null;
    this.ngZone.run(() => {
      this.loading = true;
      this.errorMsg = '';
    });

    if (this.currentMesh) {
      this.scene.remove(this.currentMesh);
      this.currentMesh.geometry.dispose();
      (this.currentMesh.material as THREE.Material).dispose();
      this.currentMesh = null;
    }

    const loader = new STLLoader();
    loader.load(
      url,
      (geometry) => {
        geometry.computeBoundingBox();
        geometry.center();

        const material = new THREE.MeshPhongMaterial({
          color: 0xEEFBFD,
          specular: 0x1a1a1a,
          shininess: 12,
          side: THREE.DoubleSide
        });

        this.currentMesh = new THREE.Mesh(geometry, material);
        this.scene.add(this.currentMesh);
        this.fitCamera();

        this.ngZone.run(() => { this.loading = false; });
      },
      undefined,
      () => {
        this.ngZone.run(() => {
          this.loading = false;
          this.errorMsg = 'No se pudo cargar el archivo';
        });
      }
    );
  }

  private fitCamera(): void {
    if (!this.currentMesh) return;

    const box = new THREE.Box3().setFromObject(this.currentMesh);
    const size = new THREE.Vector3();
    box.getSize(size);

    const fovHalfRad = THREE.MathUtils.degToRad(this.camera.fov / 2);
    const aspect = this.camera.aspect;

    // Distance required to fit height and width within the camera FOV
    const distH = (size.y / 2) / Math.tan(fovHalfRad);
    const distW = (size.x / 2) / (Math.tan(fovHalfRad) * aspect);
    const fitDist = Math.max(distH, distW, size.z * 0.5) * 1.18;

    this.camera.position.set(0, 0, fitDist);
    this.camera.near = fitDist * 0.001;
    this.camera.far = fitDist * 10;
    this.camera.updateProjectionMatrix();
    this.controls.target.set(0, 0, 0);
    this.controls.update();
  }

  private animate(): void {
    this.animationId = requestAnimationFrame(() => this.animate());
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.animationId);
    this.resizeObserver?.disconnect();
    if (this.renderer) this.renderer.dispose();
  }
}
